// @ts-nocheck
/**
 * Custom elements for upsell product carousel functionality.
 *
 * @example
 * <rt-product-upsell-carousel data-products="123456789,987654321">
 *   <div class="rt-upsell-carousel">
 *     <div class="rt-upsell-carousel__viewport">
 *       <div class="rt-upsell-carousel__container"></div>
 *     </div>
 *   </div>
 * </rt-product-upsell-carousel>
 *
 * // The carousel will fetch recommendations for each product ID
 * // and render them using the rt-upsell-product template
 */

import EmblaCarousel from 'embla-carousel';

/**
 * Product Upsell Carousel Custom Element
 * Fetches product recommendations and displays them in an Embla carousel.
 *
 * @fires rt-upsell-carousel:loaded - When recommendations are loaded
 * @fires rt-upsell-carousel:error - When an error occurs
 */
class RtProductUpsellCarousel extends HTMLElement {
  static tagName = 'rt-product-upsell-carousel';

  /** @type {import('embla-carousel').EmblaCarouselType | null} */
  embla = null;

  /** @type {AbortController | null} */
  _abortController = null;

  /** @type {IntersectionObserver | null} */
  _visibilityObserver = null;

  /** @type {Map<string, object[]>} */
  _recommendationsCache = new Map();

  /** @type {number} */
  LIMIT_PER_PRODUCT = 5;

  /** @type {string[]} */
  static EXCLUDED_TAGS = [
    'search spring exclusion',
    'hide-from-search',
    'hide-search',
    'hide_expose',
  ];

  constructor() {
    super();
    this._products = [];
    this._recommendations = [];
    this._isLoading = false;
  }

  static get observedAttributes() {
    return ['data-products'];
  }

  connectedCallback() {
    this._parseProducts();
    this._init();
  }

  disconnectedCallback() {
    this._destroyCarousel();
    this._abortController?.abort();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-products' && oldValue !== newValue && oldValue !== null) {
      this._parseProducts();
      this._init();
    }
  }

  /**
   * Parse product IDs from the data-products attribute
   */
  _parseProducts() {
    const productsAttr = this.getAttribute('data-products') || '';
    this._products = productsAttr
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
  }

  /**
   * Initialize the carousel by fetching recommendations
   */
  async _init() {
    if (this._products.length === 0) {
      this._renderEmpty('No products specified');
      return;
    }

    this._setLoading(true);

    try {
      await this._fetchAllRecommendations();
      this._renderProducts();
      this._initCarousel();

      this.dispatchEvent(
        new CustomEvent('rt-upsell-carousel:loaded', {
          bubbles: true,
          detail: { recommendations: this._recommendations },
        })
      );
    } catch (error) {
      console.error('Error loading upsell recommendations:', error);
      this._renderEmpty('Failed to load recommendations');

      this.dispatchEvent(
        new CustomEvent('rt-upsell-carousel:error', {
          bubbles: true,
          detail: { error },
        })
      );
    } finally {
      this._setLoading(false);
    }
  }

  /**
   * Set loading state
   * @param {boolean} isLoading
   */
  _setLoading(isLoading) {
    this._isLoading = isLoading;
    if (isLoading) {
      this.setAttribute('loading', '');
    } else {
      this.removeAttribute('loading');
    }
  }

  /**
   * Fetch recommendations for all product IDs
   * Limits to 5 recommendations per product
   */
  async _fetchAllRecommendations() {
    // Cancel any pending requests
    this._abortController?.abort();
    this._abortController = new AbortController();

    const allRecommendations = [];
    const seenProductIds = new Set();

    // Fetch recommendations for each product
    const fetchPromises = this._products.map((productId) =>
      this._fetchRecommendations(productId, this.LIMIT_PER_PRODUCT)
    );

    const results = await Promise.allSettled(fetchPromises);

    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        for (const product of result.value) {
          // Avoid duplicate products
          if (!seenProductIds.has(product.id)) {
            // Exclude products with restricted tags
            const productTags = (product.tags || []).map((tag) => tag.toLowerCase());
            const isExcluded = RtProductUpsellCarousel.EXCLUDED_TAGS.some((tag) =>
              productTags.includes(tag.toLowerCase())
            );

            if (!isExcluded) {
              seenProductIds.add(product.id);
              allRecommendations.push(product);
            }
          }
        }
      }
    }

    console.log('Fetched recommendations:', allRecommendations);

    this._recommendations = allRecommendations;
  }

  /**
   * Fetch product recommendations from Shopify API
   * @param {string} productId - The product ID to get recommendations for
   * @param {number} limit - Maximum number of recommendations
   * @returns {Promise<object[]>} Array of recommended products
   */
  async _fetchRecommendations(productId, limit = 3) {
    // Check cache first
    const cacheKey = `${productId}-${limit}`;
    if (this._recommendationsCache.has(cacheKey)) {
      return this._recommendationsCache.get(cacheKey);
    }

    const rootUrl = window.Shopify?.routes?.root || '/';

    // Try complementary first
    let url = `${rootUrl}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`;

    let response = await fetch(url, {
      signal: this._abortController?.signal,
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Product ${productId} not found for recommendations`);
        return [];
      }
      throw new Error(`Failed to fetch recommendations: ${response.statusText}`);
    }

    let data = await response.json();
    let products = data.products || [];

    // If complementary is empty, try related
    if (products.length === 0) {
      url = `${rootUrl}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`;

      response = await fetch(url, {
        signal: this._abortController?.signal,
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.warn(`Product ${productId} not found for recommendations`);
          return [];
        }
        throw new Error(`Failed to fetch recommendations: ${response.statusText}`);
      }

      data = await response.json();
      products = data.products || [];
    }

    // Cache the results
    this._recommendationsCache.set(cacheKey, products);

    return products;
  }

  /**
   * Render all recommended products into the carousel
   */
  _renderProducts() {
    const container = this.querySelector('.rt-upsell-carousel__container');
    if (!container) {
      console.warn('Carousel container not found');
      return;
    }

    // Clear existing slides
    container.innerHTML = '';

    if (this._recommendations.length === 0) {
      this._renderEmpty('No recommendations available');
      return;
    }

    // Create slides for each recommendation
    for (const product of this._recommendations) {
      const slide = document.createElement('div');
      slide.className = 'rt-upsell-carousel__slide';

      // Create the upsell product element
      const productElement = document.createElement('rt-upsell-product');
      productElement.setProduct(product);

      slide.appendChild(productElement);
      container.appendChild(slide);
    }
  }

  /**
   * Render an empty state message
   * @param {string} message
   */
  _renderEmpty(message) {
    const container = this.querySelector('.rt-upsell-carousel__container');
    if (container) {
      container.innerHTML = `<div class="rt-upsell-carousel__empty">${message}</div>`;
    }
  }

  /**
   * Initialize the Embla carousel
   */
  _initCarousel() {
    this._destroyCarousel();

    const viewport = this.querySelector('.rt-upsell-carousel__viewport');
    if (!viewport || this._recommendations.length === 0) {
      return;
    }

    // Check for free drag option (default: false)
    const freeDragAttr = this.getAttribute('data-free-drag');
    const dragFree = freeDragAttr !== null && freeDragAttr !== 'false';

    this.embla = EmblaCarousel(viewport, {
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: dragFree,
      loop: false,
    });

    // Reinit when the element becomes visible (e.g. when the cart drawer opens).
    // Embla cannot calculate scroll dimensions on a hidden element, so we
    // observe the first intersection and call reInit() at that point.
    this._visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && this.embla) {
          this.embla.reInit();
          this._visibilityObserver?.disconnect();
          this._visibilityObserver = null;
        }
      },
      { threshold: 0.01 }
    );
    this._visibilityObserver.observe(this);

    // Setup navigation
    // Find buttons within the parent section to avoid conflicts with multiple carousels
    const parentSection = this.closest('section') || this.parentElement;
    const prevBtn = parentSection?.querySelector(this.getAttribute('data-nav-prev-selector'));
    const nextBtn = parentSection?.querySelector(this.getAttribute('data-nav-next-selector'));

    if (prevBtn && nextBtn) {
      prevBtn.addEventListener('click', () => this.embla?.scrollPrev());
      nextBtn.addEventListener('click', () => this.embla?.scrollNext());

      // Update button states
      const updateButtons = () => {
        if (!this.embla) {
          return;
        }
        prevBtn.disabled = !this.embla.canScrollPrev();
        nextBtn.disabled = !this.embla.canScrollNext();
      };

      this.embla.on('select', updateButtons);
      this.embla.on('init', updateButtons);
      updateButtons();
    }
  }

  /**
   * Destroy the Embla carousel instance
   */
  _destroyCarousel() {
    this._visibilityObserver?.disconnect();
    this._visibilityObserver = null;
    if (this.embla) {
      this.embla.destroy();
      this.embla = null;
    }
  }

  /**
   * Refresh the carousel with new products
   * @param {string[]} productIds - Array of product IDs
   */
  refresh(productIds) {
    this.setAttribute('data-products', productIds.join(','));
  }
}

// Register the custom elements
if (!customElements.get(RtProductUpsellCarousel.tagName)) {
  customElements.define(RtProductUpsellCarousel.tagName, RtProductUpsellCarousel);
}

export { RtProductUpsellCarousel };
